import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import { auth, onAuthStateChanged, signOut as firebaseSignOut } from '../services/firebase';
import { apiFetch } from '../services/api';

type UserRole = 'admin' | 'user';

interface AuthContextType {
    user: User | null;
    role: UserRole | null;
    loading: boolean;
    isAdmin: boolean;
    signOut: () => Promise<void>;
    updateRole: (newRole: UserRole) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [role, setRole] = useState<UserRole | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                setUser(firebaseUser);
                setLoading(true);
                try {
                    // Sync profile and get role from our custom backend
                    const data = await apiFetch('/auth/sync-profile', {
                        method: 'POST'
                    });
                    setRole(data.profile?.role || 'user');
                } catch (error) {
                    console.error('Error syncing profile:', error);
                    setRole('user');
                } finally {
                    setLoading(false);
                }
            } else {
                setUser(null);
                setRole(null);
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, []);

    const signOut = async () => {
        await firebaseSignOut(auth);
        setUser(null);
        setRole(null);
    };

    const value = {
        user,
        role,
        loading,
        isAdmin: role === 'admin',
        signOut,
        updateRole: setRole,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
